import { useMemo, useState } from 'react';
import type { Finding, LintResult, Severity } from '../lib/types.js';
import { Empty, Pill } from './ui.js';

/**
 * Rendering-issue panel.
 *
 * Each row answers three questions in order, because that is the order a
 * reviewer asks them: *where* is it, *what* is wrong, and *what do I change*.
 * An earlier version led with the measurement -- "#111111 on a dark background
 * is only 1.1:1" -- which named neither the line it referred to nor the value to
 * use instead, and four lines sharing a colour produced four identical rows.
 *
 * Findings are grouped by rule so a repeated problem reads as one item with a
 * count, and each occurrence keeps its own location and its own fix.
 */

const CATEGORY_LABELS: Record<string, string> = {
  images: 'Images',
  responsive: 'Layout',
  'dark-mode': 'Dark mode',
  accessibility: 'Accessibility',
  'merge-fields': 'Merge fields',
  typography: 'Typography',
  compatibility: 'Client support',
};

export function LintSummary({ lint }: { lint: LintResult | null }): JSX.Element {
  if (!lint) return <Pill tone="outline">not checked</Pill>;
  const { error, warning, info } = lint.counts;

  if (error === 0 && warning === 0) {
    return (
      <Pill tone="ok">
        {info > 0 ? `clean, ${info} note${info === 1 ? '' : 's'}` : 'no issues'}
      </Pill>
    );
  }
  return (
    <span className="row">
      {error > 0 && <Pill tone="danger">{error} blocking</Pill>}
      {warning > 0 && <Pill tone="warn">{warning} warning{warning === 1 ? '' : 's'}</Pill>}
      {info > 0 && <Pill tone="outline">{info} note{info === 1 ? '' : 's'}</Pill>}
    </span>
  );
}

type Level = Severity | 'all';

interface Group {
  rule: string;
  severity: Severity;
  category: string;
  findings: Finding[];
}

export function LintPanel({
  lint,
  onSelectBlock,
  selectedBlockId,
}: {
  lint: LintResult | null;
  onSelectBlock?: (blockId: string) => void;
  selectedBlockId?: string | null;
}): JSX.Element {
  const [level, setLevel] = useState<Level>('all');

  const groups = useMemo<Group[]>(() => {
    if (!lint) return [];
    const rank: Record<Severity, number> = { error: 0, warning: 1, info: 2 };
    const visible =
      level === 'all' ? lint.findings : lint.findings.filter((f) => rank[f.severity] <= rank[level]);

    const byRule = new Map<string, Group>();
    for (const finding of visible) {
      const existing = byRule.get(finding.rule);
      if (existing) {
        existing.findings.push(finding);
      } else {
        byRule.set(finding.rule, {
          rule: finding.rule,
          severity: finding.severity,
          category: finding.category,
          findings: [finding],
        });
      }
    }
    return [...byRule.values()];
  }, [lint, level]);

  if (!lint) {
    return <Empty title="Not checked yet">Render a preview to run the rendering checks.</Empty>;
  }

  if (lint.findings.length === 0) {
    return (
      <Empty title="Nothing to fix">
        {lint.imagesInspected > 0
          ? `Checked the layout, colours, merge fields and ${lint.imagesInspected} image file${lint.imagesInspected === 1 ? '' : 's'}.`
          : 'Checked the layout, colours and merge fields. Run the full check to inspect the image files too.'}
      </Empty>
    );
  }

  return (
    <>
      <div
        className="row wrap"
        style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}
      >
        <div className="seg">
          {(['all', 'error', 'warning'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className={level === option ? 'on' : ''}
              onClick={() => setLevel(option)}
            >
              {option === 'all'
                ? 'Everything'
                : option === 'error'
                  ? 'Must fix'
                  : 'Must fix + should fix'}
            </button>
          ))}
        </div>
        <span className="spacer" />
        <span className="tiny muted">
          {lint.imagesInspected > 0
            ? `${lint.imagesInspected} image file(s) checked`
            : 'structure only'}
        </span>
      </div>

      <ul className="lint-list">
        {groups.map((group) => (
          <GroupRow
            key={group.rule}
            group={group}
            selectedBlockId={selectedBlockId}
            onSelect={onSelectBlock}
          />
        ))}
      </ul>
    </>
  );
}

const SEVERITY_WORD: Record<Severity, string> = {
  error: 'must fix',
  warning: 'should fix',
  info: 'note',
};

function GroupRow({
  group,
  selectedBlockId,
  onSelect,
}: {
  group: Group;
  selectedBlockId?: string | null;
  onSelect?: (blockId: string) => void;
}): JSX.Element {
  const repeated = group.findings.length > 1;
  // A repeated problem starts collapsed: the point is that it is one problem in
  // several places, not several problems.
  const [expanded, setExpanded] = useState(!repeated);
  const first = group.findings[0]!;

  return (
    <li className="lint-item" style={{ cursor: 'default' }}>
      <div className="top">
        <span className={`sev ${group.severity}`}>{SEVERITY_WORD[group.severity]}</span>
        <span className="msg">
          {repeated ? (
            <>
              <strong>{groupTitle(group)}</strong>
              <span className="muted"> — {group.findings.length} places</span>
            </>
          ) : (
            <>
              {first.where && <strong>{capitalise(first.where)}: </strong>}
              {first.message}
            </>
          )}
        </span>
        {repeated && (
          <button className="ghost sm" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Hide detail' : 'Show detail'}
          </button>
        )}
      </div>

      {/*
        Locations are always visible, never behind the toggle. "Dark mode: text
        invisible - 3 places" without them answers none of the question it
        raises; the whole point of grouping is to say WHICH three. The toggle
        only controls the per-occurrence wording and suggested value.
      */}
      {repeated && !expanded && (
        <div className="tiny" style={{ marginTop: 4 }}>
          {group.findings.map((finding, i) => (
            <span key={i}>
              {i > 0 && <span className="muted">, </span>}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (finding.blockId && onSelect) onSelect(finding.blockId);
                }}
                style={{
                  cursor: finding.blockId && onSelect ? 'pointer' : 'default',
                  fontWeight: finding.blockId === selectedBlockId ? 700 : 400,
                }}
                title={finding.blockId ? 'Show this block in the editor' : undefined}
              >
                {finding.where ?? 'the signature'}
              </a>
            </span>
          ))}
        </div>
      )}

      {!repeated && <Detail finding={first} />}

      {repeated && expanded && (
        <div style={{ marginTop: 8, borderLeft: '2px solid var(--border)', paddingLeft: 10 }}>
          {group.findings.map((finding, i) => (
            <div key={i} style={{ marginBottom: i === group.findings.length - 1 ? 0 : 10 }}>
              <div
                className="row"
                style={{
                  cursor: finding.blockId && onSelect ? 'pointer' : 'default',
                  background:
                    finding.blockId && finding.blockId === selectedBlockId
                      ? 'var(--accent-soft)'
                      : undefined,
                  borderRadius: 4,
                  padding: '1px 3px',
                }}
                onClick={() => {
                  if (finding.blockId && onSelect) onSelect(finding.blockId);
                }}
                title={finding.blockId ? 'Show this block in the editor' : undefined}
              >
                <strong className="tiny">{capitalise(finding.where ?? 'the signature')}</strong>
              </div>
              <Detail finding={finding} compact />
            </div>
          ))}
        </div>
      )}

      <div className="row wrap" style={{ marginTop: 6 }}>
        <span className="rule">{group.rule}</span>
        <span className="spacer" />
        <span className="tiny muted">{CATEGORY_LABELS[group.category] ?? group.category}</span>
      </div>

      {first.clients && first.clients.length > 0 && (
        <div className="clients">
          {first.clients.map((client) => (
            <Pill key={client} tone="outline">
              {client}
            </Pill>
          ))}
        </div>
      )}
    </li>
  );
}

/** The action, then the reason. Never the other way round. */
function Detail({ finding, compact }: { finding: Finding; compact?: boolean }): JSX.Element {
  return (
    <>
      {compact && <div className="tiny muted">{finding.message}</div>}
      {finding.fix && (
        <div
          className="tiny"
          style={{
            marginTop: 4,
            padding: '5px 8px',
            borderRadius: 4,
            background: 'var(--surface-3)',
            color: 'var(--text)',
            display: 'flex',
            gap: 6,
          }}
        >
          <span aria-hidden="true" style={{ color: 'var(--accent)', fontWeight: 700 }}>
            Fix
          </span>
          <span>{finding.fix}</span>
        </div>
      )}
      {finding.hint && (
        <div className="hint" style={{ marginTop: 4 }}>
          {finding.hint}
        </div>
      )}
    </>
  );
}

/**
 * A heading for a repeated problem, derived from the rule name rather than from
 * one of the messages.
 *
 * The messages carry instance-specific values -- "#111111", "10px" -- so reusing
 * the first one as a group heading would state one occurrence's colour as though
 * it applied to all of them. The rule name is the one thing every member of the
 * group genuinely shares.
 */
function groupTitle(group: Group): string {
  const name = (group.rule.split('/')[1] ?? group.rule).replace(/-/g, ' ');
  const category = CATEGORY_LABELS[group.category] ?? group.category;
  return `${category}: ${name}`;
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
